<template>
  <page-main title="Career Pathway Analyzer" noBottom >
    <template v-slot:aside>
      <div>
        <p>
          Not sure what kinds of jobs to seek out? Use this tool to evaluate
          your resume and career interests and receive customized suggestions
          and feedback.
        </p>
        <ul>
          <li>
            Select one of your saved resumes from the dropdown and, optionally,
            enter up to five career interests — then submit for a customized
            response.
          </li>
          <li>
            Be sure to download your report if you want to keep it. Career
            pathway analyses are not saved by Resumation.
          </li>
        </ul>
        <div class="aside-chips">
          <span class="aside-chip">★ Plus only</span>
        </div>
      </div>
    </template>
    <template v-slot:nav>
      <modal cardTitle="Tips" ctaText="Need a few tips?" :accentButton="true">
        <ul>
          <li>
            <span class="font-weight-bold">Tip #1:</span> Mix and match your
            saved resumes with your career interests to receive different job
            recommendations.
          </li>
          <li>
            <span class="font-weight-bold">Tip #2:</span> Don't know what
            version of your resume to use? Use the one with the most information
            to get started.
          </li>
          <li>
            <span class="font-weight-bold">Tip #3:</span> Use this feature to
            get ideas on what jobs to consider. Once you have some suggestions,
            use Resumation's Job Fit Analyzer tool to see how your resume stacks
            up against any job description (and receive suggestions on how it
            can improve)!
          </li>
        </ul>
      </modal>
      <v-btn outlined class="mt-4" block @click="navToContactUs()">
        Any feedback? Contact us!
      </v-btn>
    </template>
    <v-container fluid class="container-main">
      <v-row>
        <v-col cols="12">
          <v-select
            v-model="selectedResume"
            :items="resumeOptions"
            item-text="name"
            item-value="id"
            label="Select saved resume or full profile"
            tabindex="1"
            @change="resumeChange"
            autofocus
          ></v-select>
        </v-col>
        <v-col cols="12">
          <multi-select
            label="Career Interests"
            :selected.sync="careerInterestsLocal"
            :optionsList="baseCareerInterests"
            tabindex="2"
            :maxSelections="25"
            autofocus
          ></multi-select>
        </v-col>
        <v-col
          cols="12"
          class="text-center d-flex justify-space-between align-center"
        >
          <v-btn
            :disabled="!selectedResume || isAnalyzing"
            :class="{ 'grey-btn': !selectedResume }"
            @click="optimizeWithAI"
            tabindex="3"
          >
            Submit
          </v-btn>
        </v-col>
        <v-col cols="12" style=" padding-bottom: 48px;">
          <v-card
            v-if="isAnalyzing || aiOptimizedPhrasing || showAiOptimizationError"
            class="pa-3"
          >
            <v-row>
              <v-col cols="12" class="d-flex justify-space-between">
                <span><b>Career Pathway Analysis</b></span>
                <v-btn
                  v-if="
                    !isAnalyzing &&
                      aiOptimizedPhrasing &&
                      !showAiOptimizationError
                  "
                  small
                  @click="downloadReport"
                  >CLICK TO DOWNLOAD ANALYSIS
                </v-btn>
              </v-col>
              <v-col cols="12">
                <div v-if="isAnalyzing" class="text-center py-6">
                  <v-progress-circular indeterminate color="primary" size="48" class="mb-3"></v-progress-circular>
                  <p class="text-body-1 font-weight-medium mb-1">Analyzing...</p>
                  <p class="text-body-2 font-italic text--secondary mb-0">
                    This may take a minute to complete
                  </p>
                </div>
                <div v-else-if="showAiOptimizationError">
                  <v-card-text
                    class="text--primary text-body-1 pt-4 pb-3 text-center"
                  >
                    <h3 class="pt-2">Oops. Something went wrong.</h3>
                    <h3 class="text-body-1 font-italic text--secondary">
                      {{ errorMessage }}
                    </h3>
                  </v-card-text>
                  <v-card-actions
                    :class="
                      $vuetify.breakpoint.xsOnly
                        ? 'flex-wrap justify-center'
                        : 'flex-wrap justify-end'
                    "
                  >
                  </v-card-actions>
                </div>
                <div v-else>
                  <p>Generated: {{ new Date().toLocaleDateString() }}</p>
                  <p>Based on Resume: {{ selectedResumeName }}</p>
                  <v-alert type="info" outlined dense class="mb-4 text-body-2">
                    Please note that this assessment is based on the information provided in the job description and resume. It is not professional advice, and the accuracy of the assessment is subjective and may be incorrect. Users should use this assessment with caution and seek professional advice.
                  </v-alert>
                  <v-card v-if="profileSummaryText" outlined class="mb-3">
                    <v-card-text class="text--primary text-body-2 pt-3">
                      <span v-html="formatLine(profileSummaryText)"></span>
                    </v-card-text>
                  </v-card>
                  <v-card
                    v-for="(section, i) in filteredSections"
                    :key="i"
                    outlined
                    class="mb-3"
                  >
                    <v-card-title v-if="section.header" class="text-subtitle-1 font-weight-bold py-2" style="word-break: break-word;">
                      {{ section.header }}
                    </v-card-title>
                    <v-divider v-if="section.header"></v-divider>
                    <v-card-text class="text--primary text-body-2 pt-3">
                      <div
                        v-for="(line, j) in section.lines"
                        :key="j"
                        :class="{ 'mb-2': line !== '' }"
                      >
                        <span v-html="formatLine(line)"></span>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          class="text-center"
          v-if="aiOptimizedPhrasing && !isAnalyzing && !showAiOptimizationError"
        >
          <v-btn @click="resetDialog = true">Reset</v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="resetDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline"
          >Are you sure you want to reset this page?</v-card-title
        >
        <v-card-text
          >You will lose the analysis you generated. Make sure you download your
          analysis if you want to reference it later.</v-card-text
        >
        <v-card-actions>
          <v-btn color="blue darken-1" text @click="resetResumeState"
            >No - Return to Analysis</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="resetCareerReport"
            >Yes - Reset</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- PR-UPGRADE-MODAL: ad-hoc premium r-dialog replaced by the shared
         UpgradeToPlusModal (mounted globally in App.vue). Gating below is
         unchanged; it opens the shared modal via `upgradeModal/open`. -->
  </page-main>
</template>

<script>
import MultiSelect from "@/components/MultiSelect.vue";
import * as resApi from "../../api/ResumeApi.js";
import { GetProfileContext } from "../../api/ProfileContextApi";
import * as log from "../../utility/logging/logger.js";
import { mapFields } from "vuex-map-fields";
import { mapActions } from "vuex";
import * as Display from "../../utility/display.js";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import CareerReportBuilder from "../../docxBuilder/careerReportBuilder.js";
import { saveAs } from "file-saver";

export default {
  data: () => ({
    aiOptimizedPhrasing: "",
    aiOptimizedDisplay: "",
    phrasing: "",
    context: "career-pathway-analyzer",
    aiOptimizationStatus: "idle",
    errorMessage: "",
    selectedResume: "",
    previousSelectedResume: "",
    isAnalyzing: false,
    resetDialog: false,
    resetResume: true,
    careerInterestsLocal: [],
    careerInterestsDisplayLocal: "",
  }),
  components: {
    "multi-select": MultiSelect,
  },
  methods: {
    // PR-UPGRADE-MODAL: open the one shared upgrade prompt for this tool.
    // Replaces the local r-dialog + navToPremiumPage redirect. Gating
    // (when this is called) is unchanged.
    openUpgrade() {
      this.dialog = false;
      this.$store.dispatch("upgradeModal/open", {
        featureName: "the Career Pathway Analyzer",
        message:
          "Career Pathway runs on the AI tools. Plus turns them on, along with unlimited Sage coaching, whenever you want more."
      });
    },
    nav(routerPath) {
      this.$router.push({ path: routerPath });
    },
    ...mapActions("auth", ["refreshPremium"]),
    ...mapActions("userSubmissions", ["loadResumeSubmissions"]),
    ...mapActions("careerInterest", ["saveCareerInterests"]),
    resetCareerReport() {
      this.aiOptimizationStatus = "idle";
      this.resetDialog = false;
      this.isAnalyzing = false;
      this.aiOptimizedPhrasing = "";
      if (this.resetResume) {
        this.selectedResume = "";
      }
      this.resetResume = true;
    },
    resetResumeState() {
      this.selectedResume = this.previousSelectedResume;
      this.resetDialog = false;
      this.resetResume = true;
    },
    resumeChange() {
      if (this.aiOptimizedPhrasing !== "" || this.isAnalyzing == true) {
        this.resetDialog = true;
        this.resetResume = false;
      } else {
        this.previousSelectedResume = this.selectedResume;
      }
    },
    buildReportDisplay() {
      const text = this.aiOptimizedPhrasing;
      const lines = text.split("\n");
      const sections = [];
      let current = { header: "", lines: [] };

      for (let line of lines) {
        const trimmed = line.trim();
        if (this.isSectionHeader(trimmed)) {
          if (current.header || current.lines.length > 0) {
            sections.push(current);
          }
          let header = trimmed;
          header = header.replace(/^##\s*/, "");
          header = header.replace(/^\*\*(.*?)\*\*:?$/, "$1");
          header = header.replace(/:$/, "");
          current = { header: header, lines: [] };
        } else {
          current.lines.push(trimmed);
        }
      }
      if (current.header || current.lines.length > 0) {
        sections.push(current);
      }
      this.aiOptimizedDisplay = sections;
    },
    isSectionHeader(line) {
      if (!line || line.length === 0) return false;
      if (line.startsWith("## ")) return true;
      if (/^\*\*.+\*\*:?\s*$/.test(line)) return true;
      if (line.length > 2 && line === line.toUpperCase() && /[A-Z]/.test(line) && !/^\d/.test(line)) return true;
      if (/^[A-Z][A-Za-z\s&,/()-]+:\s*$/.test(line) && line.length < 80) return true;
      return false;
    },
    formatLine(line) {
      if (!line) return "";
      let escaped = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      escaped = escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      escaped = escaped.replace(/(?:^|\s)-\s+\*([^*]+)\*/g, " - <em>$1</em>");
      return escaped;
    },
    async downloadReport() {
      try {
        log.logInformation("Building report docx.");
        const builder = new CareerReportBuilder();
        builder.CreateText(this.aiOptimizedPhrasing, this.selectedResumeName);
        const blob = await builder.BuildDocx("Career Pathway Analysis");
        saveAs(blob, "CareerPathwayAnalysis.docx");
      } catch (e) {
        log.logError("Failed to build downloaded docx", e);
        this.aiOptimizationStatus = "error";
        this.errorMessage = "Error Building downloadable docx.";
      }
    },
    async optimizeWithAI() {
      this.isAnalyzing = true;
      try {
        log.logInformation("Refreshing user premium and attempts status...");
        await this.refreshPremium();

        if (!this.isPremium) {
          this.openUpgrade();
          this.isAnalyzing = false;
        } else {
          let reportData = await this.buildReportDataString();
          if (reportData == "Error") {
            return null;
          }
          let result = await resApi.FetchAiOptimizedPhrasing({
            resumeId: this.$store.state.resumeId,
            phrasing: reportData,
            context: this.context,
          });
          if (!result.success) {
            log.logError(
              "Failed to get AI optimized description",
              "optimizeWithAI"
            );
            this.aiOptimizationStatus = "error";
            this.errorMessage = "Failed to get AI Response";
            if (result.displayText == "Max Attempts Reached") {
              this.errorMessage =
                "Failed to get AI Response: Max usage reached.";
            }
          } else {
            if (this.isAnalyzing == true) {
              this.aiOptimizedPhrasing = result.data.optimizedPhrasing;
              this.buildReportDisplay();
            }
          }
          this.isAnalyzing = false;
          await this.refreshPremium();
        }
      } catch (e) {
        log.logError("Failed to get AI optimized description", e);
        this.aiOptimizationStatus = "error";
      }
    },
    async buildReportDataString() {
      if (this.selectedResume === "__full_profile__") {
        return await this.buildProfileDataString();
      }
      const resume = this.resumes.find((r) => r.id === this.selectedResume);
      if (!resume) {
        return "Error";
      }
      const fileRef = resume.fileRef;
      const response = await fetch(fileRef);
      const arrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });
      const resumeText = doc.getFullText();
      this.findDisplayValues();
      const careerInterestsText = this.careerInterestsDisplayLocal || "(none provided)";
      const today = new Date().toISOString().slice(0, 10);
      let result = "As-of date: " + today + "\n" +
        "Career Interests: " + careerInterestsText + "\n\n" +
        "Resume Content:\n" + resumeText;
      return result;
    },
    async buildProfileDataString() {
      try {
        const result = await GetProfileContext(this.$store.state.resumeId);
        if (!result.success || !result.data) return "Error";
        const d = result.data;
        const today = new Date().toISOString().slice(0, 10);
        let text = "As-of date: " + today + "\n";

        const name = [d.name?.firstName, d.name?.lastName].filter(Boolean).join(" ");
        if (name) text += "Name: " + name + "\n";

        if (d.address) {
          const loc = [d.address.city, d.address.state, d.address.country].filter(Boolean).join(", ");
          if (loc) text += "Location: " + loc + "\n";
        }

        if (d.professionalSummary) text += "Professional Summary: " + d.professionalSummary + "\n";

        this.findDisplayValues();
        text += "Career Interests: " + (this.careerInterestsDisplayLocal || "(none provided)") + "\n";

        if (d.skills?.hard?.length) text += "Hard Skills: " + d.skills.hard.join(", ") + "\n";
        if (d.skills?.soft?.length) text += "Soft Skills: " + d.skills.soft.join(", ") + "\n";
        if (d.skills?.technical?.length) text += "Technical Skills: " + d.skills.technical.join(", ") + "\n";
        if (d.adjectives?.length) text += "Self-Description: " + d.adjectives.join(", ") + "\n";
        if (d.languages?.length) text += "Languages: " + d.languages.join(", ") + "\n";

        if (d.workExperience?.length) {
          const ordered = d.workExperience.slice().sort(function(a, b) {
            if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
            return (b.endDate || b.startDate || "").localeCompare(a.endDate || a.startDate || "");
          });
          text += "Work Experience (most recent first):\n";
          for (const job of ordered) {
            const title = job.positions?.[0]?.title || job.positions?.[0]?.role || "Role";
            const company = job.company || "Unknown Company";
            const startDate = job.startDate || "?";
            const endDate = job.isCurrent ? "Present" : (job.endDate || "?");
            const statusTag = job.isCurrent ? "[CURRENT ROLE]" : "[PAST ROLE]";
            text += `  - ${statusTag} ${title} at ${company} (${startDate} – ${endDate})\n`;
            if (job.achievements?.length) {
              for (const ach of job.achievements) {
                const achText = ach.useCustom ? (ach.displayCustom || ach.displayAuto) : (ach.displayAuto || ach.displayCustom || ach.description || "");
                if (!achText) continue;
                text += `    Accomplishment: ${achText}\n`;
                if (ach.background) text += `      Context: ${ach.background}\n`;
                if (ach.actionResult) text += `      Action & Result: ${ach.actionResult}\n`;
                if (ach.measurement) text += `      Impact: ${ach.measurement}\n`;
              }
            }
          }
        }

        if (d.education?.length) {
          text += "Education:\n";
          for (const edu of d.education) {
            const degree = edu.degree || "";
            const field = edu.fieldOfStudy ? " in " + edu.fieldOfStudy : "";
            const school = edu.school ? " from " + edu.school : "";
            const endDate = edu.endDate || edu.graduationDate || "";
            let statusTag, dateText;
            if (endDate && endDate <= today) {
              statusTag = "[GRADUATED]";
              dateText = endDate.slice(0, 4);
            } else if (endDate) {
              statusTag = "[IN PROGRESS]";
              dateText = "expected " + endDate.slice(0, 4);
            } else if (edu.startDate) {
              statusTag = "[IN PROGRESS]";
              dateText = "started " + edu.startDate.slice(0, 4);
            } else {
              statusTag = "[STATUS UNKNOWN]";
              dateText = "date not specified";
            }
            text += `  - ${statusTag} ${(degree + field).trim()}${school} (${dateText})\n`;
          }
        }

        if (d.certifications?.length) text += "Certifications: " + d.certifications.join(", ") + "\n";

        if (d.fitPreferences?.length) {
          text += "Fit & Preferences:\n";
          for (const item of d.fitPreferences) {
            const selected = (item.selected || []).join(", ");
            text += `  - ${item.question || item.questionId}: ${selected}\n`;
          }
        }
        if (d.fitPreferencesNotes) text += "Fit Preferences Notes: " + d.fitPreferencesNotes + "\n";

        if (d.smallWins?.length) {
          const top = d.smallWins.slice(0, 10);
          text += `Recent Wins (${d.smallWins.length} total, showing up to 10):\n`;
          for (const win of top) {
            if (!win.description) continue;
            text += `  - ${win.description}${win.eventDate ? " (" + win.eventDate + ")" : ""}\n`;
            if (win.context) text += `    Situation: ${win.context}\n`;
            if (win.action) text += `    Action: ${win.action}\n`;
            if (win.outcome) text += `    Outcome: ${win.outcome}\n`;
          }
        }

        return text.trim();
      } catch (e) {
        log.logError("Failed to build profile data string", e);
        return "Error";
      }
    },
    async loadData() {
      try {
        await this.refreshPremium();
        await this.loadResumeSubmissions();
        this.careerInterestsLocal = [...this.careerInterests];
      } catch (e) {
        log.logError("Failed to load resumes", e);
      }
    },
    findDisplayValues() {
      this.careerInterestsDisplayLocal = Display.BuildCommaString(
        "",
        this.careerInterestsLocal
      );
    },
    navToContactUs() {
      window.open("https://www.resumation.net/contact", "_blank");
    },
  },
  computed: {
    resumeOptions() {
      const options = [{ id: "__full_profile__", name: "Full Resumation Profile (all your data)" }];
      if (this.resumes && this.resumes.length) {
        options.push(...this.resumes);
      }
      return options;
    },
    selectedResumeName() {
      if (this.previousSelectedResume === "__full_profile__") return "Full Resumation Profile";
      const resume = this.resumes?.find(
        (r) => r.id === this.previousSelectedResume
      );
      return resume ? resume.name : "No resume selected";
    },
    parsedSections() {
      if (!this.aiOptimizedDisplay || !Array.isArray(this.aiOptimizedDisplay)) return [];
      return this.aiOptimizedDisplay;
    },
    profileSummaryText() {
      var sections = this.parsedSections;
      if (!sections || sections.length === 0) return "";
      var first = sections[0];
      if (first.header) return "";
      var disclaimerPattern = /please note that this assessment/i;
      var lines = first.lines || [];
      var summaryLines = [];
      for (var i = 0; i < lines.length; i++) {
        if (disclaimerPattern.test(lines[i])) continue;
        if (lines[i].trim()) summaryLines.push(lines[i]);
      }
      return summaryLines.join(" ").trim();
    },
    filteredSections() {
      var sections = this.parsedSections;
      if (!sections || sections.length === 0) return [];
      var result = [];
      var disclaimerPattern = /please note that this assessment/i;
      for (var i = 0; i < sections.length; i++) {
        var section = sections[i];
        if (!section.header) {
          var filteredLines = [];
          for (var j = 0; j < (section.lines || []).length; j++) {
            if (!disclaimerPattern.test(section.lines[j]) && section.lines[j].trim()) {
              filteredLines.push(section.lines[j]);
            }
          }
          if (filteredLines.length > 0 && this.profileSummaryText) continue;
          if (filteredLines.length > 0) {
            result.push({ header: section.header, lines: filteredLines });
          }
        } else {
          result.push(section);
        }
      }
      return result;
    },
    showAiOptimizationError() {
      return this.aiOptimizationStatus === "error";
    },
    ...mapFields("auth", {
      isPremium: "isPremium",
      remainingAiOptimizerAttempts: "remainingAiOptimizerAttempts",
      maxCareerPathwayAttempts: "maxCareerPathwayAttempts",
      careerPathwayAttempts: "careerPathwayAttempts",
    }),
    ...mapFields("userSubmissions", {
      resumes: "resumes",
    }),
    ...mapFields("careerInterest", ["careerInterests", "baseCareerInterests"]),
  },
  async mounted() {
    await this.loadData();
    if (!this.isPremium) {
      this.openUpgrade();
    }
  },
};
</script>
